/**
 * Lightweight localStorage cache so users can re-open their saved trips
 * even without network connectivity. We deliberately keep this small
 * (no IndexedDB) — trips are tiny JSON snapshots.
 */
const KEY = "globr.offline.trips";
const MAX_TRIPS = 25;
const CONFLICT_KEY = "globr.offline.conflicts";
const PENDING_DELETE_KEY = "globr.offline.pendingDeletes";
const STALE_TTL_MS = 6 * 60 * 60 * 1000; // 6h — trips fresher than this are skipped during incremental sync

export interface OfflineTrip {
  id: string;
  from_city: string;
  to_city: string;
  start_date: string;
  end_date: string;
  travelers: number;
  transport?: string | null;
  itinerary?: any;
  saved_at: string;
  /** Last time we successfully reconciled with the server (ISO). */
  synced_at?: string;
  /** Server `updated_at` we last saw. */
  remote_updated_at?: string;
  /** True if user edited this trip while offline → needs upload / conflict check. */
  dirty?: boolean;
  /** Local edit timestamp when `dirty` is true. */
  local_modified_at?: string;
}

export interface OfflineConflict {
  id: string;
  /** Snapshot of the local version at the moment of conflict. */
  local: OfflineTrip;
  /** Server snapshot that won. */
  remote: OfflineTrip;
  detected_at: string;
}

function read(): OfflineTrip[] {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr : [];
  } catch { return []; }
}

function write(list: OfflineTrip[]) {
  try { localStorage.setItem(KEY, JSON.stringify(list.slice(0, MAX_TRIPS))); }
  catch { /* quota — ignore silently */ }
}

export function listOfflineTrips(): OfflineTrip[] {
  return read().sort((a, b) => (b.saved_at || "").localeCompare(a.saved_at || ""));
}

export function saveOfflineTrip(trip: Omit<OfflineTrip, "saved_at">) {
  const list = read().filter((t) => t.id !== trip.id);
  const now = new Date().toISOString();
  list.unshift({ ...trip, saved_at: now, synced_at: trip.synced_at ?? now });
  write(list);
}

/**
 * Mark an offline trip as locally edited. The next sync will compare against
 * the server `updated_at` and trigger conflict resolution if both diverged.
 */
export function markOfflineTripDirty(id: string, patch?: Partial<OfflineTrip>) {
  const list = read();
  const idx = list.findIndex((t) => t.id === id);
  if (idx === -1) return;
  list[idx] = {
    ...list[idx],
    ...patch,
    dirty: true,
    local_modified_at: new Date().toISOString(),
  };
  write(list);
}

export function removeOfflineTrip(id: string) {
  write(read().filter((t) => t.id !== id));
  // Also drop any pending-delete marker for this id.
  setPendingDeletes(getPendingDeletes().filter((p) => p.id !== id));
}

export function isOfflineTrip(id: string): boolean {
  return read().some((t) => t.id === id);
}

/**
 * Wipe every locally-cached trip snapshot, conflict record and pending-delete
 * marker. MUST be called on sign-out: `/offline` is a public route (works
 * without a session), so leaving a previous user's cached itineraries in
 * localStorage after logout would expose them to the next person using a
 * shared/public device.
 */
export function clearOfflineTrips(): void {
  try {
    localStorage.removeItem(KEY);
    localStorage.removeItem(CONFLICT_KEY);
    localStorage.removeItem(PENDING_DELETE_KEY);
    localStorage.removeItem(SYNC_KEY);
  } catch { /* ignore — best effort */ }
}

import { supabase } from "@/integrations/supabase/client";

const SYNC_KEY = "globr.offline.lastSync";

export function getLastSync(): string | null {
  try { return localStorage.getItem(SYNC_KEY); } catch { return null; }
}

// ---- Conflicts ----
function readConflicts(): OfflineConflict[] {
  try {
    const raw = localStorage.getItem(CONFLICT_KEY);
    const arr = raw ? JSON.parse(raw) : [];
    return Array.isArray(arr) ? arr : [];
  } catch { return []; }
}
function writeConflicts(list: OfflineConflict[]) {
  try { localStorage.setItem(CONFLICT_KEY, JSON.stringify(list.slice(0, 50))); } catch { /* ignore */ }
}
export function listConflicts(): OfflineConflict[] {
  return readConflicts().sort((a, b) => (b.detected_at || "").localeCompare(a.detected_at || ""));
}
export function dismissConflict(id: string) {
  writeConflicts(readConflicts().filter((c) => c.id !== id));
}

// ---- Server-side deletions awaiting user confirmation ----
export interface PendingDelete { id: string; label: string; detected_at: string; }
function getPendingDeletes(): PendingDelete[] {
  try {
    const raw = localStorage.getItem(PENDING_DELETE_KEY);
    const arr = raw ? JSON.parse(raw) : [];
    return Array.isArray(arr) ? arr : [];
  } catch { return []; }
}
function setPendingDeletes(list: PendingDelete[]) {
  try { localStorage.setItem(PENDING_DELETE_KEY, JSON.stringify(list.slice(0, 50))); } catch { /* ignore */ }
}
export function listPendingDeletes(): PendingDelete[] { return getPendingDeletes(); }
export function clearPendingDelete(id: string) {
  setPendingDeletes(getPendingDeletes().filter((p) => p.id !== id));
}

/**
 * Re-fetch cached trips from the backend and refresh local snapshots.
 *
 * Optimisations:
 *  - Incremental: only revalidate trips older than `STALE_TTL_MS` (or never synced),
 *    plus any locally dirty trip. Pass `force: true` to bypass.
 *  - Conflict detection: when a trip is `dirty` and the server `updated_at` advanced
 *    since the last sync, we keep the server version (server-wins) and store a
 *    backup of the local version in `listConflicts()` for the user to inspect.
 *  - Server-deleted trips are NOT auto-removed: they are queued in
 *    `listPendingDeletes()` so the UI can ask the user to confirm.
 */
export async function syncOfflineTrips(
  opts: { force?: boolean } = {}
): Promise<{ updated: number; conflicts: number; pendingDeletes: number; checked: number; total: number }> {
  const force = !!opts.force;
  const list = read();
  if (list.length === 0) return { updated: 0, conflicts: 0, pendingDeletes: 0, checked: 0, total: 0 };
  if (typeof navigator !== "undefined" && !navigator.onLine) {
    return { updated: 0, conflicts: 0, pendingDeletes: 0, checked: 0, total: list.length };
  }

  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.user) return { updated: 0, conflicts: 0, pendingDeletes: 0, checked: 0, total: list.length };

  // Incremental selection: dirty trips, never-synced trips, or trips synced > TTL ago.
  const now = Date.now();
  const candidates = list.filter((t) => {
    if (force) return true;
    if (t.dirty) return true;
    if (!t.synced_at) return true;
    const age = now - new Date(t.synced_at).getTime();
    return Number.isNaN(age) || age > STALE_TTL_MS;
  });

  if (candidates.length === 0) {
    try { localStorage.setItem(SYNC_KEY, new Date().toISOString()); } catch { /* ignore */ }
    return { updated: 0, conflicts: 0, pendingDeletes: getPendingDeletes().length, checked: 0, total: list.length };
  }

  const ids = candidates.map((t) => t.id);
  const { data, error } = await supabase
    .from("trips")
    .select("id, from_city, to_city, start_date, end_date, travelers, transport, itinerary, updated_at")
    .in("id", ids);

  if (error) throw error;

  const fresh = new Map<string, any>((data || []).map((t: any) => [t.id, t]));
  let updated = 0;
  let conflicts = 0;
  const conflictBuf = readConflicts();
  const pendingDel = getPendingDeletes();

  const stampNow = new Date().toISOString();
  const next: OfflineTrip[] = list.map((cached) => {
    // Untouched trip (not in the candidates batch): keep as-is.
    if (!ids.includes(cached.id)) return cached;

    const remote = fresh.get(cached.id);
    if (!remote) {
      // Server-deleted: do NOT drop. Queue for user confirmation, keep local copy.
      if (!pendingDel.some((p) => p.id === cached.id)) {
        pendingDel.push({
          id: cached.id,
          label: `${cached.from_city} → ${cached.to_city}`,
          detected_at: stampNow,
        });
      }
      return cached;
    }

    const remoteSig = JSON.stringify(remote.itinerary || []);
    const localSig = JSON.stringify(cached.itinerary || []);
    const remoteStamp = remote.updated_at || stampNow;
    const fieldsChanged =
      remoteSig !== localSig ||
      remote.from_city !== cached.from_city ||
      remote.to_city !== cached.to_city ||
      remote.start_date !== cached.start_date ||
      remote.end_date !== cached.end_date ||
      remote.travelers !== cached.travelers ||
      (remote.transport ?? null) !== (cached.transport ?? null);

    const remoteAdvanced =
      !cached.remote_updated_at || (remote.updated_at && remote.updated_at > cached.remote_updated_at);

    // Conflict: local has unsynced edits AND server moved forward independently.
    if (cached.dirty && remoteAdvanced && fieldsChanged) {
      conflicts++;
      conflictBuf.unshift({
        id: cached.id,
        local: { ...cached },
        remote: {
          id: remote.id,
          from_city: remote.from_city,
          to_city: remote.to_city,
          start_date: remote.start_date,
          end_date: remote.end_date,
          travelers: remote.travelers,
          transport: remote.transport,
          itinerary: remote.itinerary,
          saved_at: remoteStamp,
        },
        detected_at: stampNow,
      });
      // Server wins (per project policy) — keep local snapshot in conflict log.
    }

    if (fieldsChanged) updated++;

    return {
      id: remote.id,
      from_city: remote.from_city,
      to_city: remote.to_city,
      start_date: remote.start_date,
      end_date: remote.end_date,
      travelers: remote.travelers,
      transport: remote.transport,
      itinerary: remote.itinerary,
      saved_at: fieldsChanged ? remoteStamp : cached.saved_at,
      synced_at: stampNow,
      remote_updated_at: remoteStamp,
      dirty: false,
      local_modified_at: undefined,
    };
  });

  write(next);
  writeConflicts(conflictBuf);
  setPendingDeletes(pendingDel);
  try { localStorage.setItem(SYNC_KEY, new Date().toISOString()); } catch { /* ignore */ }
  return {
    updated,
    conflicts,
    pendingDeletes: pendingDel.length,
    checked: candidates.length,
    total: next.length,
  };
}